// ANA SAYFA CANLI SOHBET — Sohbetle Üret ana sayfada GERÇEKTEN çalışır (Auren 2026-07-16).
// Giriş yapmış kullanıcı burada asistanla konuşur (yazı+mikrofon+foto); prompt hazır olunca
// sohbet Stüdyo'ya TAŞINIR (sessionStorage) ve üretim orada tamamlanır. Misafir: kibar giriş uyarısı.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { logClientError } from "@/lib/errorLog";
import { Loader2, Send, Mic, Paperclip, Coins, Wand2, X, MonitorUp, Camera, Download } from "lucide-react";

type Msg = { role: "user" | "assistant"; content: string };
const MSG_COST = 2;

// 📲 PWA yükleme olayı MODÜL yüklenir yüklenmez yakalanır — bileşen mount'unu
// bekleyen listener olayı kaçırıyordu (beforeinstallprompt bir kez ateşlenir).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pwaInstallEvt: any = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); pwaInstallEvt = e;
    // Tarayıcı bu olayı gönderiyorsa uygulama YÜKLÜ DEĞİL — eski "yüklendi" notu bayatlamış demektir (silinmiş)
    try { localStorage.removeItem("pwa-installed"); } catch { /* yok say */ }
  });
  window.addEventListener("appinstalled", () => { pwaInstallEvt = null; try { localStorage.setItem("pwa-installed", "true"); } catch { /* yok say */ } });
}

export function HeroChat() {
  const { user, refreshCredits } = useAuth();
  const navigate = useNavigate();
  const [chat, setChat] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState<{ englishPrompt: string; sceneTurkish: string; turkishDialogue: string } | null>(null);
  const [consented, setConsented] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const pendingMsg = useRef("");
  const listRef = useRef<HTMLDivElement>(null);
  // 📎 foto (asistan görür, üretimde ürün referansı olur)
  const [imgUrl, setImgUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // 🎤 mikrofon (FreeformStudio ile aynı desen — ref üzerinden hep güncel gönderim)
  const [recording, setRecording] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const recogRef = useRef<any>(null);
  const sendRef = useRef<((t: string) => void) | null>(null);
  const voiceSupported = typeof window !== "undefined" && ("webkitSpeechRecognition" in window || "SpeechRecognition" in window);
  // 📲 PWA yükleme (ekran paylaşımı notu)
  const installApp = async () => {
    // 1) Yükleme olayı elimizdeyse uygulama kesin yüklü değil → direkt tarayıcının penceresini aç
    if (pwaInstallEvt) {
      pwaInstallEvt.prompt();
      const choice = await pwaInstallEvt.userChoice.catch(() => null);
      if (choice?.outcome === "accepted") toast.success("Uygulama yükleniyor 🎉");
      pwaInstallEvt = null;
      return;
    }
    // 2) Şu an uygulamanın İÇİNDEysek kesin yüklü (localStorage notuna güvenme — silinince haber gelmez)
    if (window.matchMedia?.("(display-mode: standalone)").matches) {
      toast.success("Zaten uygulamanın içindesin 🎉");
      return;
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) toast.info("iPhone/iPad: alttaki Paylaş simgesi → 'Ana Ekrana Ekle' ile yükle.");
    else toast.info("Tarayıcı menüsünden (⋮ veya adres çubuğundaki yükleme simgesi) 'Uygulamayı yükle'yi seç.");
  };
  // Canlı özellik rozetleri Stüdyo'ya götürür (ekran/kamera paylaşımı orada başlar)
  const goStudioFeature = () => { if (guestGate()) return; navigate("/serbest-uretim"); };

  // SADECE sohbet kutusunu kaydır — scrollIntoView tüm SAYFAYI kaydırıyordu (2026-07-16 bug)
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [chat, busy]);
  useEffect(() => () => { try { recogRef.current?.stop(); } catch { /* yok say */ } }, []);

  const guestGate = (): boolean => {
    if (user) return false;
    if (input.trim()) sessionStorage.setItem("freeform_seed_text", input.trim());
    toast.info("Kullanmak için ücretsiz kayıt ol / giriş yap 🎁 İlk videon bizden — yazdıkların hazır bekliyor.");
    navigate("/auth?mode=signup", { state: { from: "/serbest-uretim" } });
    return true;
  };

  const doSend = async (msg: string) => {
    const newChat: Msg[] = [...chat, { role: "user", content: msg }];
    setChat(newChat);
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("freeform-assist", {
        body: { messages: newChat, durationSec: 20, productImageUrl: imgUrl || undefined },
      });
      if (data?.code === "plan_required") { toast.error("Sohbetle Üret için Başlangıç veya üst paket gerekir."); setTimeout(() => navigate("/pricing"), 1200); return; }
      if (data?.code === "insufficient_credits") { toast.error("Krediniz yetersiz."); setTimeout(() => navigate("/pricing"), 1200); return; }
      if (error || !data?.ok) throw new Error(data?.error || error?.message || "Asistan yanıt veremedi");
      refreshCredits().catch(() => {});
      setChat((c) => [...c, { role: "assistant", content: data.reply || "…" }]);
      if (data.ready && (data.englishPrompt || data.turkishDialogue)) {
        setReady({ englishPrompt: data.englishPrompt || "", sceneTurkish: data.sceneTurkish || "", turkishDialogue: data.turkishDialogue || "" });
        toast.success("Senaryon hazır ✨ Stüdyoda üretime geçebilirsin.");
      }
    } catch (e) {
      logClientError("sohbetle-uret", `anasayfa: ${e instanceof Error ? e.message : "bilinmiyor"}`);
      setChat((c) => [...c, { role: "assistant", content: "Bir hata oldu, tekrar dener misin?" }]);
    } finally { setBusy(false); }
  };

  const send = (override?: string) => {
    const msg = (typeof override === "string" ? override : input).trim();
    if (!msg) return;
    if (guestGate()) return;
    setInput("");
    if (!consented) { pendingMsg.current = msg; setConsentOpen(true); return; }
    doSend(msg);
  };
  sendRef.current = send;

  const toggleMic = () => {
    if (guestGate()) return;
    if (recording) { try { recogRef.current?.stop(); } catch { /* yok say */ } setRecording(false); return; }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SR) { toast.error("Tarayıcın sesli girişi desteklemiyor."); return; }
    const r = new SR();
    r.lang = "tr-TR"; r.interimResults = false; r.continuous = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.onresult = (e: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const text = Array.from(e.results).map((x: any) => x[0].transcript).join(" ").trim();
      if (text) sendRef.current?.(text);
    };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    recogRef.current = r;
    try { r.start(); setRecording(true); } catch { setRecording(false); }
  };

  const onFile = async (f: File) => {
    if (guestGate()) return;
    setUploading(true);
    try {
      const ext = f.name.split(".").pop() || "jpg";
      const path = `${user!.id}/urun-hero-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("products").upload(path, f, { upsert: true });
      if (error) { toast.error("Görsel yüklenemedi"); return; }
      setImgUrl(supabase.storage.from("products").getPublicUrl(path).data.publicUrl);
      toast.success("Fotoğraf eklendi 📎 Asistan ürününü görecek.");
    } finally { setUploading(false); }
  };

  // Sohbeti + hazır promptu Stüdyo'ya taşı → orada Üret'e basar (kredi onayı orada)
  const goStudio = () => {
    sessionStorage.setItem("freeform_seed_chat", JSON.stringify(chat));
    if (ready) sessionStorage.setItem("freeform_seed_ready", JSON.stringify(ready));
    if (imgUrl) sessionStorage.setItem("freeform_seed_image", imgUrl);
    sessionStorage.setItem("freeform_intent_sent", String(Date.now()));
    navigate("/serbest-uretim");
  };

  return (
    <div className="mx-auto mt-2 w-full max-w-xl rounded-2xl border-2 border-primary/30 bg-card p-3 shadow-soft text-left">
      {chat.length > 0 && (
        <div ref={listRef} className="mb-2 max-h-56 overflow-y-auto space-y-2 pr-1">
          {chat.map((m, i) => (
            <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[85%] ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-muted"}`}>{m.content}</div>
          ))}
          {busy && <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Hazırlıyor…</div>}
        </div>
      )}
      {imgUrl && (
        <div className="mb-2 flex items-center gap-2">
          <img src={imgUrl} alt="Ürün" className="h-10 w-10 rounded-md border object-cover" />
          <span className="text-xs text-muted-foreground flex-1">Fotoğraf ekli</span>
          <button type="button" onClick={() => setImgUrl("")} title="Kaldır" className="rounded-full border p-1 hover:bg-muted"><X className="h-3 w-3" /></button>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ""; }} />
      <div className="flex items-center gap-1.5">
        <Button type="button" onClick={() => { if (!guestGate()) fileRef.current?.click(); }} disabled={uploading} size="icon" variant="outline" title="Ürün fotoğrafı ekle" className="shrink-0">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </Button>
        <textarea value={input} onChange={(e) => setInput(e.target.value.slice(0, 300))}
          onInput={(e) => { const t = e.currentTarget; t.style.height = "auto"; t.style.height = Math.min(t.scrollHeight, 128) + "px"; }}
          placeholder={recording ? "Dinliyorum… konuş" : "Ürününü ya da fikrini yaz — reklamını birlikte üretelim…"}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={busy} rows={1}
          className="min-w-0 flex-1 resize-none max-h-32 overflow-y-auto border-0 shadow-none focus-visible:ring-0 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-muted-foreground" />
        {voiceSupported && (
          <Button type="button" onClick={toggleMic} size="icon" variant={recording ? "default" : "outline"} title="Konuşarak anlat"
            className={`shrink-0 ${recording ? "animate-pulse bg-red-600 hover:bg-red-700 text-white" : ""}`}>
            <Mic className="h-4 w-4" />
          </Button>
        )}
        <Button onClick={() => send()} disabled={busy || !input.trim()} variant="hero" className="shrink-0 gap-1.5">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="hidden sm:inline">✨ Sohbetle Üret</span>
        </Button>
      </div>
      {(chat.length > 0 || ready) && (
        <Button onClick={goStudio} variant={ready ? "gradient" : "outline"} size="sm" className="mt-2 w-full gap-1.5">
          <Wand2 className="h-4 w-4" />{ready ? "Senaryon hazır — Stüdyoda Üret 🎬" : "Stüdyoda devam et (avatar/mekân seç, üret)"}
        </Button>
      )}

      {/* Neler yapabilir? — özellik rozetleri (ekran/kamera Stüdyo'da başlar) */}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"><Mic className="h-3 w-3" />Sesli sohbet</span>
        <button type="button" onClick={goStudioFeature} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[11px] text-primary hover:bg-primary/10 transition-colors"><MonitorUp className="h-3 w-3" />Canlı ekran paylaşımı</button>
        <button type="button" onClick={goStudioFeature} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[11px] text-primary hover:bg-primary/10 transition-colors"><Camera className="h-3 w-3" />Canlı kamera</button>
        <span className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-[11px] text-muted-foreground"><Paperclip className="h-3 w-3" />Ürün fotoğrafı</span>
      </div>
      <button type="button" onClick={installApp} className="mt-1.5 w-full text-center text-[11px] text-muted-foreground hover:text-foreground transition-colors">
        <Download className="inline h-3 w-3 mr-1 align-text-bottom" />Ekran paylaşımında en iyi sonuç için <span className="underline underline-offset-2 font-medium">uygulamayı yükle</span>
      </button>

      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Coins className="h-4 w-4 text-primary" />Kredi bilgilendirmesi</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Asistana attığın <b>her mesaj {MSG_COST} kredi</b>. Video üretimi süreye göre <b>40 / 80 / 120 / 160 kredi</b>. Devam edelim mi?</p>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setConsentOpen(false)}>İptal</Button>
            <Button variant="gradient" onClick={() => { setConsentOpen(false); setConsented(true); if (pendingMsg.current) { doSend(pendingMsg.current); pendingMsg.current = ""; } }}>Onaylıyorum</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
